import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { AccordionItem, Button, Label, Select } from '../../../components/ui';
import { fetcher } from '../../../services/fetcher';
import { bulkUpdateAccAccounts } from '../../../services/mutations';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';

type Lookup = { id: number; code: string; name: string; isActive?: boolean };

type Props = {
  accountIds: number[];
  disabled?: boolean;
  onUpdated: () => void;
};

const NO_CHANGE = '__no_change__';

const boolOptions = [
  { value: NO_CHANGE, label: 'No change' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const toLookupOptions = (raw: { data?: Lookup[] } | Lookup[] | undefined) => {
  const list = (Array.isArray(raw) ? raw : raw?.data ?? []).filter((t) => t.isActive !== false);
  return [
    { value: NO_CHANGE, label: 'No change' },
    { value: '', label: 'Clear (None)' },
    ...list
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((t) => ({ value: String(t.id), label: t.name })),
  ];
};

export const CoaBulkUpdateBar: React.FC<Props> = ({ accountIds, disabled, onUpdated }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [operationalTypeId, setOperationalTypeId] = useState(NO_CHANGE);
  const [accountTypeId, setAccountTypeId] = useState(NO_CHANGE);
  const [departmentId, setDepartmentId] = useState(NO_CHANGE);
  const [allowOpenItems, setAllowOpenItems] = useState(NO_CHANGE);
  const [isIntercompany, setIsIntercompany] = useState(NO_CHANGE);
  const [isBudget, setIsBudget] = useState(NO_CHANGE);
  const [isActive, setIsActive] = useState(NO_CHANGE);
  const [saving, setSaving] = useState(false);

  const { data: opTypesRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>(
    '/acc-operational-types?page=1&limit=500',
    fetcher,
  );
  const { data: fsTypesRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>(
    '/acc-account-types?page=1&limit=500',
    fetcher,
  );
  const { data: deptsRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>('/departments?page=1&limit=500', fetcher);

  const operationalTypeOptions = useMemo(() => toLookupOptions(opTypesRaw), [opTypesRaw]);
  const accountTypeOptions = useMemo(() => toLookupOptions(fsTypesRaw), [fsTypesRaw]);

  const deptOptions = useMemo(() => {
    const list = Array.isArray(deptsRaw) ? deptsRaw : deptsRaw?.data ?? [];
    return [
      { value: NO_CHANGE, label: 'No change' },
      { value: '', label: 'Clear (None)' },
      ...list
        .filter((t) => t.isActive !== false)
        .map((t) => ({ value: String(t.id), label: t.name })),
    ];
  }, [deptsRaw]);

  const hasChanges =
    operationalTypeId !== NO_CHANGE ||
    accountTypeId !== NO_CHANGE ||
    departmentId !== NO_CHANGE ||
    allowOpenItems !== NO_CHANGE ||
    isIntercompany !== NO_CHANGE ||
    isBudget !== NO_CHANGE ||
    isActive !== NO_CHANGE;

  const reset = () => {
    setOperationalTypeId(NO_CHANGE);
    setAccountTypeId(NO_CHANGE);
    setDepartmentId(NO_CHANGE);
    setAllowOpenItems(NO_CHANGE);
    setIsIntercompany(NO_CHANGE);
    setIsBudget(NO_CHANGE);
    setIsActive(NO_CHANGE);
  };

  const onApply = async () => {
    if (!hasChanges || accountIds.length === 0) return;
    setSaving(true);
    try {
      const payload: Parameters<typeof bulkUpdateAccAccounts>[0] = { ids: accountIds };
      if (operationalTypeId !== NO_CHANGE) {
        payload.operationalTypeId = operationalTypeId === '' ? null : Number(operationalTypeId);
      }
      if (accountTypeId !== NO_CHANGE) {
        payload.accountTypeId = accountTypeId === '' ? null : Number(accountTypeId);
      }
      if (departmentId !== NO_CHANGE) {
        payload.departmentId = departmentId === '' ? null : Number(departmentId);
      }
      if (allowOpenItems !== NO_CHANGE) payload.allowOpenItems = allowOpenItems === 'true';
      if (isIntercompany !== NO_CHANGE) payload.isIntercompany = isIntercompany === 'true';
      if (isBudget !== NO_CHANGE) payload.isBudget = isBudget === 'true';
      if (isActive !== NO_CHANGE) payload.isActive = isActive === 'true';

      const result = await bulkUpdateAccAccounts(payload);
      showSuccess(`Updated ${result.updated} account${result.updated === 1 ? '' : 's'}`);
      reset();
      onUpdated();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string | string[] } }; message?: string };
      const msg = err?.response?.data?.message;
      showError(
        Array.isArray(msg) ? msg.join(', ') : msg || err?.message || 'Bulk update failed.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (accountIds.length === 0) return null;

  const countLabel = `${accountIds.length} account${accountIds.length === 1 ? '' : 's'}`;

  return (
    <div className={cn('flex-shrink-0 px-3 py-2 border-b', colors.border)}>
      <AccordionItem
        defaultOpen={false}
        title={
          <span className="inline-flex items-center gap-2">
            <span className={cn('text-sm font-medium', colors.text)}>Update all</span>
            {hasChanges && (
              <span className={cn('text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5', colors.info)}>
                Pending
              </span>
            )}
          </span>
        }
        description={`Bulk-edit ${countLabel} in the current list`}
        className={cn('rounded-lg shadow-none', colors.border, colors.surface)}
        headerClassName={cn(colors.surfaceHover)}
      >
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Operational type</Label>
              <Select
                value={operationalTypeId}
                onChange={(e) => setOperationalTypeId(e.target.value)}
                options={operationalTypeOptions}
              />
            </div>
            <div>
              <Label>Account type (FS)</Label>
              <Select
                value={accountTypeId}
                onChange={(e) => setAccountTypeId(e.target.value)}
                options={accountTypeOptions}
              />
            </div>
            <div>
              <Label>Department</Label>
              <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} options={deptOptions} />
            </div>
            <div>
              <Label>Allow open items</Label>
              <Select value={allowOpenItems} onChange={(e) => setAllowOpenItems(e.target.value)} options={boolOptions} />
            </div>
            <div>
              <Label>Intercompany</Label>
              <Select value={isIntercompany} onChange={(e) => setIsIntercompany(e.target.value)} options={boolOptions} />
            </div>
            <div>
              <Label>Budget account</Label>
              <Select value={isBudget} onChange={(e) => setIsBudget(e.target.value)} options={boolOptions} />
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
                options={[
                  { value: NO_CHANGE, label: 'No change' },
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={reset}
              disabled={!hasChanges || saving || disabled}
            >
              Reset
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onApply}
              disabled={!hasChanges || saving || disabled}
            >
              {saving ? 'Updating…' : `Update ${countLabel}`}
            </Button>
          </div>
        </div>
      </AccordionItem>
    </div>
  );
};
